import { useAtomValue } from 'jotai'
import type { MapboxGeoJSONFeature } from 'mapbox-gl'
import { useCallback } from 'react'
import { getBoundsOfFeature } from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import { propertyMapInstanceAtom, propertyMapPaddingRefAtom } from '@app/state/propertyMapAtoms'
import {
  FitBoundsOfFeatureOptions,
  FlyTo,
  handleFitBounds,
  handleFlyTo,
} from '@app/contexts/ModuleMapContexts/utils/location'

export const usePropertyMapNavigation = () => {
  const map = useAtomValue(propertyMapInstanceAtom)
  const mapPaddingRef = useAtomValue(propertyMapPaddingRefAtom)

  const getMapPadding = useCallback(
    (usePadding: boolean | undefined) =>
      usePadding || usePadding === undefined
        ? mapPaddingRef.current
        : { top: 0, right: 0, bottom: 0, left: 0 },
    [mapPaddingRef],
  )

  const flyTo = useCallback(
    (options: FlyTo) => {
      if (!map) return

      handleFlyTo({
        flyTo: {
          padding: getMapPadding(options.usePadding),
          ...options,
        },
        map,
      })
    },
    [map, getMapPadding],
  )

  const fitToBoundsOfFeature = useCallback(
    (
      feature: MapboxGeoJSONFeature | GeoJSON.Feature,
      { usePadding }: FitBoundsOfFeatureOptions,
    ) => {
      if (!map) return

      const boundingBox = getBoundsOfFeature(feature)

      if (!boundingBox) return

      handleFitBounds({
        boundingBox: [
          boundingBox.min_coord[0],
          boundingBox.min_coord[1],
          boundingBox.max_coord[0],
          boundingBox.max_coord[1],
        ],
        map,
        padding: getMapPadding(usePadding),
      })
    },
    [map, getMapPadding],
  )

  return {
    flyTo,
    fitToBoundsOfFeature,
    getMapPadding,
  }
}
